import React, { useCallback, useState } from 'react';
import { Button } from '@gafe/common-ui';
import { find } from 'lodash';

import { WarningPopOver } from '@/components/generic/WarningPopOver';
import {
  markStepAsCompleted,
  useOrderContext,
  useOrderDispatch,
} from '@/components/modals/SubmitOrder/helpers';
import { ContactVm, OrderDeliveryType } from '@/types';

import { getStepByName } from '../../helpers';
import { StepsName } from '../../types';
import { ProposalDetails } from '../Common/ProposalDetails';

import { AddressBookModal } from './components/AddressBook';
import { AddressBookModalViews } from './components/AddressBook/types';
import { DeliveryDetails } from './components/DeliveryDetails';
import { OrderDetails } from './components/OrderDetails';
import { ShippingDetails } from './components/ShippingDetails';
import { Props } from './types';

import modalStyles from '../../SubmitOrderModal.module.scss';

export const ShippingStep = ({
  warehouseAddresses,
  goToStep,
  close,
  allShippingAddresses,
  stepName,
}: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const [isAddressBookModalOpen, setAddressBookModalOpen] = useState(false);
  const orderDetailsStep = getStepByName(state, StepsName['Order Details']);
  const confirmationStep = getStepByName(
    state,
    state.isAccessProgram ? StepsName['Request Contract'] : StepsName['Confirmation'],
  );

  const selectAddress = useCallback(
    (shipmentContact: ContactVm): void => {
      const warehouseAddress = find(
        allShippingAddresses,
        sa => sa.contactId === shipmentContact.contactId,
      );

      const deliveryType = warehouseAddress
        ? OrderDeliveryType['Company/warehouse address']
        : OrderDeliveryType['Homeowner/installation address'];

      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          deliveryType,
          shipmentContact,
        },
      });
    },
    [dispatch, allShippingAddresses],
  );
  const onModalClose = useCallback(() => {
    setAddressBookModalOpen(false);
  }, []);

  const onAddressBookOpen = useCallback(() => {
    setAddressBookModalOpen(true);
  }, []);

  const goToOrderDetails = useCallback(() => {
    goToStep?.(orderDetailsStep);
  }, [orderDetailsStep, goToStep]);

  const goToConfirmation = useCallback(() => {
    goToStep?.(confirmationStep);
    markStepAsCompleted(state.wizardHeaderSteps, stepName);
  }, [confirmationStep, goToStep, state.wizardHeaderSteps, stepName]);

  const initialModalView =
    warehouseAddresses.length > 0
      ? AddressBookModalViews.AddressList
      : AddressBookModalViews.AddressForm;

  const isNextStepDisabled = !state.order.shipmentContact;
  const errors = isNextStepDisabled ? ['shipping address'] : [];

  return (
    <div>
      <ProposalDetails
        orderItems={state.visibleOrderItems}
        purchaseNumber={state.order.purchaseNumber}
        shouldDisplayPurchaseNumber
      />
      <OrderDetails goToOrderDetails={goToOrderDetails} />
      <ShippingDetails
        openAddressBook={onAddressBookOpen}
        selectAddress={selectAddress}
        selectedAddress={state.order.shipmentContact}
        warehouseAddresses={warehouseAddresses}
      />
      <DeliveryDetails />
      <AddressBookModal
        closeModal={onModalClose}
        initialModalView={initialModalView}
        isModalOpen={isAddressBookModalOpen}
        selectAddress={selectAddress}
        selectedAddress={state.order.shipmentContact}
      />
      <div className={modalStyles.stepper_buttons}>
        <Button colorSchema="neutral" onClick={close} size="large" variant="text">
          Cancel
        </Button>
        <WarningPopOver productErrors={errors}>
          <Button
            colorSchema="primary"
            disabled={isNextStepDisabled}
            onClick={goToConfirmation}
            size="large"
          >
            Review Order
          </Button>
        </WarningPopOver>
      </div>
    </div>
  );
};
